<template>
  <div>
    <div class="mb-8 flex items-start justify-between">
      <div>
        <h1 class="text-3xl font-bold">
          <Link
            class="text-indigo-500 hover:text-indigo-800"
            :href="blocksRoute"
          >
            {{ blocksLabel }}
          </Link>
          <span class="font-medium text-indigo-400">/</span>
          {{ form.name || 'Nowy blok' }}
        </h1>
        <div class="mt-2">
          <span class="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {{ template.name }}
          </span>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <Link
          class="text-sm font-medium text-gray-700 hover:text-gray-900"
          :href="blocksRoute"
        >
          Anuluj
        </Link>
        <loading-button
          :loading="form.processing"
          class="btn-indigo"
          @click="submit"
        >
          Utwórz blok
        </loading-button>
      </div>
    </div>

    <div class="space-y-6">
      <div class="overflow-hidden rounded-lg border-2 border-indigo-200 bg-indigo-50 shadow-sm">
        <form @submit.prevent="submit">
          <div class="px-6 py-5">
            <text-input
              v-model="form.name"
              :errors="form.errors.name"
              label="Nazwa bloku"
            />
          </div>
        </form>
      </div>

      <div class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div class="px-6 py-5">
          <text-input
            v-model="anchorId"
            label="Identyfikator sekcji (kotwica)"
            placeholder="np. nasze-podejscie"
          />
          <p class="mt-1 text-xs text-gray-500">
            Wspólny dla obu języków. Pasek „Context Navigation Tabs” linkuje do tej
            sekcji przez <code>#identyfikator</code> — wartość musi pasować do pola
            „ID (kotwica)” wybranej zakładki. Zostaw puste, aby nie dodawać kotwicy.
          </p>
        </div>
      </div>

      <div v-if="template.schema.fields.length > 0" class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div class="flex align-items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900 px-6 py-3">Treść bloku</h2>
          <div class="flex items-center justify-between p-2 gap-2">
            <button
              type="button"
              :class="[
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                editingLocale === 'pl'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              ]"
              @click.stop.prevent="switchEditingLocale('pl')"
            >
              PL
            </button>
            <button
              type="button"
              :class="[
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                editingLocale === 'en'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              ]"
              @click.stop.prevent="switchEditingLocale('en')"
            >
              EN
            </button>
          </div>
        </div>

        <form class="px-6 py-5" @submit.prevent="submit">
          <block-schema-fields
            :fields="template.schema.fields"
            :form="form"
            :editing-locale="editingLocale"
            :linkable-pages="linkablePages"
            :get-field-value="getFieldValue"
            :set-field-value="setFieldValue"
          />
        </form>
      </div>
      <div v-else>
        <p class="p-2 text-red-500">Ten szablon nie ma pól.</p>
      </div>

      <div class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div class="border-b border-gray-200 bg-gray-50 px-6 py-4">
          <div class="flex items-center gap-2">
            <icon name="eye" class="h-5 w-5 text-indigo-500 stroke-current" />
            <h2 class="text-lg font-semibold text-gray-900">Podgląd na żywo</h2>
          </div>
          <p class="mt-1 text-xs text-gray-500">
            Zobacz, jak blok wygląda podczas edycji
          </p>
        </div>
        <div class="bg-gray-50 p-2">
          <block-preview compact :block="blockForPreview" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createLayoutHelper } from '@/utils/layoutHelper';

export default {
  layout: createLayoutHelper,
};
</script>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { Link, useForm } from '@inertiajs/vue3';
import BlockPreview from '@/Shared/BlockPreview.vue';
import BlockSchemaFields from '@/Shared/BlockSchemaFields.vue';
import Icon from '@/Shared/Icon.vue';
import LoadingButton from '@/Shared/LoadingButton.vue';
import TextInput from '@/Shared/TextInput.vue';
import { getBlocksIndexRoute, getCreateBlockRoute } from '@/utils/createBlockRouteHelper';
import { blockKindLabelPl } from '@/utils/blockKindLabels';

const { proxy } = getCurrentInstance();
const routes = proxy.$routes;

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
  kind: {
    type: String,
    default: null,
  },
  // eslint-disable-next-line vue/prop-name-casing
  site_context: {
    type: String,
    default: null,
  },
  linkablePages: {
    type: Array,
    default: () => [],
  },
});

const pagesById = computed(() =>
  Object.fromEntries(props.linkablePages.map((page) => [page.id, page])),
);

const formData = {
  name: '',
  template_id: props.template.id,
  kind: props.kind,
  images: {},
  data_pl: {},
  data_en: {},
};

if (props.template?.schema?.fields) {
  props.template.schema.fields.forEach((field) => {
    if (field.type === 'image') {
      formData.images[field.name] = null;
    } else if (field.type === 'page_link') {
      formData.data_pl[field.name] = null;
      formData.data_en[field.name] = null;
    } else if (field.type === 'resource_link') {
      formData.data_pl[field.name] = '';
      formData.data_en[field.name] = '';
    } else {
      const defaultValue = field.default !== undefined ? field.default : '';
      formData.data_pl[field.name] = defaultValue;
      formData.data_en[field.name] = defaultValue;
    }
  });
}

const form = useForm(formData);
const editingLocale = ref('pl');

// Anchor id is a structural, non-localized setting: a single input mirrored into
// both locales' data under the reserved `anchorId` key. DynamicBlock reads it to
// render the in-page `<section :id>` that Context Navigation Tabs link to.
const anchorId = computed({
  get() {
    return form.data_pl?.anchorId ?? form.data_en?.anchorId ?? '';
  },
  set(value) {
    const next = value ?? '';
    if (!form.data_pl) form.data_pl = {};
    if (!form.data_en) form.data_en = {};
    form.data_pl.anchorId = next;
    form.data_en.anchorId = next;
  },
});

const blockForPreview = computed(() => {
  const data = {};
  const currentData = form[`data_${editingLocale.value}`] || {};
  if (props.template?.schema?.fields) {
    props.template.schema.fields.forEach((field) => {
      if (field.type !== 'image') {
        const value = currentData[field.name];
        if (value !== undefined && value !== null && value !== '') {
          data[field.name] = value;
        }
      }
    });
  }

  const imagesByField = {};
  if (props.template?.schema?.fields) {
    const imageFields = props.template.schema.fields.filter((field) => field.type === 'image');
    imageFields.forEach((field) => {
      const newFile = form.images[field.name];
      if (newFile && newFile instanceof File) {
        imagesByField[field.name] = URL.createObjectURL(newFile);
      }
    });
  }

  const previewData = resolvePageLinksInData(data);
  const normalizedAnchorId = String(anchorId.value || '').trim();
  if (normalizedAnchorId) {
    previewData.anchorId = normalizedAnchorId;
  }

  return {
    id: null,
    name: form.name,
    kind: form.kind,
    anchor_id: normalizedAnchorId || null,
    data: previewData,
    images: imagesByField,
    template: props.template || null,
    site_context: props.site_context,
  };
});

const blocksLabel = computed(() => blockKindLabelPl(props?.kind));

const blocksRoute = computed(() => getBlocksIndexRoute(routes, props.site_context, props.kind));

const resolvePageLinksInData = (data) => {
  const resolved = { ...data };
  if (resolved.aktualnosciPageId != null && pagesById.value[resolved.aktualnosciPageId]) {
    resolved.aktualnosciUrl = pagesById.value[resolved.aktualnosciPageId].path;
  }
  if (resolved.contactPageId != null && pagesById.value[resolved.contactPageId]) {
    resolved.contactUrl = pagesById.value[resolved.contactPageId].path;
  }
  if (resolved.buttonPageId != null && pagesById.value[resolved.buttonPageId]) {
    resolved.buttonUrl = pagesById.value[resolved.buttonPageId].path;
  }
  return resolved;
};

const getFieldValue = (fieldName) => {
  const localeData = form[`data_${editingLocale.value}`] || {};
  return localeData[fieldName] ?? null;
};

const setFieldValue = (fieldName, value) => {
  if (!form[`data_${editingLocale.value}`]) {
    form[`data_${editingLocale.value}`] = {};
  }
  form[`data_${editingLocale.value}`][fieldName] = value;
};

const switchEditingLocale = (locale) => {
  editingLocale.value = locale;
};

const submit = () => {
  const images = {};
  Object.keys(form.images).forEach((fieldName) => {
    const file = form.images[fieldName];
    if (file && file instanceof File) {
      images[fieldName] = file;
    }
  });

  const submitData = {
    name: form.name,
    template_id: form.template_id,
    kind: form.kind,
    data_pl: form.data_pl || {},
    data_en: form.data_en || {},
    images,
  };

  if (props.site_context) {
    submitData.site_context = props.site_context;
  }

  form.transform(() => submitData).post(getCreateBlockRoute(routes, props.site_context));
};
</script>
